/**
 * USER PRACTICE LIBRARY MODELS
 * 
 * Modelos para la biblioteca personal de prácticas del usuario.
 * Incluye prácticas de múltiples fuentes:
 * - Asignadas por terapeuta
 * - De cursos completados
 * - Guardadas del historial
 * - Favoritas
 */

// ============================================
// ENUMS
// ============================================

export enum PracticeSourceType {
  THERAPIST_ASSIGNED = 'therapist_assigned',
  COURSE = 'course',
  SELF_DISCOVERED = 'self_discovered',
  FROM_HISTORY = 'from_history',
  OFFICIAL_LIBRARY = 'official_library'
}

export enum PracticeDifficulty {
  EASY = 'easy',
  MEDIUM = 'medium',
  HARD = 'hard'
}

// ============================================
// USER PRACTICE LIBRARY ITEM
// ============================================

export interface UserPracticeLibraryItem {
  id: string;
  userId: string;
  
  // Origen de la práctica
  source: {
    type: PracticeSourceType;
    therapistId?: string;        // Si viene del terapeuta
    therapistName?: string;      // Nombre para mostrar
    courseId?: string;           // Si viene de un curso
    courseName?: string;         // Nombre del curso
    schoolId?: string;           // Si viene de una escuela
    schoolName?: string;
    assignmentId?: string;       // Referencia a patientPracticeAssignments
    originalPracticeId?: string; // ID de la práctica original
  };
  
  // Datos de la práctica (denormalizado para acceso rápido)
  practiceData: {
    title: string;
    description: string;
    instructions: string;
    duration: number;            // minutos
    category: string;
    categories?: string[];       // Categorías múltiples
    difficulty: PracticeDifficulty;
    tags: string[];
    
    // Multimedia opcional
    audioUrl?: string;
    videoUrl?: string;
    imageUrl?: string;
    externalUrl?: string;
    externalType?: string;
  };
  
  // Organización personal del usuario
  userOrganization: {
    isFavorite: boolean;
    personalNotes?: string;
    customTags?: string[];
    
    // Situaciones en las que el usuario usa esta práctica
    situationsForUse?: string[];  // "ansiedad", "antes de dormir", etc.
    
    // Carpeta personal (opcional)
    folderId?: string;
    folderName?: string;
  };
  
  // Permisos de IA (configurados por terapeuta o por defecto)
  aiPermissions: {
    canBeSuggestedByAI: boolean;
    requiresTherapistApproval: boolean;
    
    // Situaciones en las que la IA puede sugerirla
    approvedSituations?: AISituationType[];
    
    // Si fue aprobada por terapeuta
    approvedByTherapist?: boolean;
    approvedAt?: string;
    approvedBy?: string;
  };
  
  // Estadísticas personales del usuario con esta práctica
  stats: {
    timesCompleted: number;
    lastCompletedAt?: string;
    avgRating: number;           // Promedio de valoraciones (1-5)
    avgMoodImprovement?: number; // Mejora promedio de mood (moodAfter - moodBefore)
    totalMinutes: number;        // Tiempo total dedicado
  };
  
  // Estado
  isActive: boolean;
  isArchived: boolean;
  
  // Metadata
  addedAt: string;
  updatedAt: string;
}

// ============================================
// AI SITUATION TYPES
// ============================================

export enum AISituationType {
  ANXIETY_MILD = 'anxiety_mild',
  ANXIETY_MODERATE = 'anxiety_moderate',
  STRESS = 'stress',
  SLEEP_ISSUES = 'sleep_issues',
  LOW_MOOD = 'low_mood',
  FOCUS_ISSUES = 'focus_issues',
  OVERWHELM = 'overwhelm',
  ANGER = 'anger',
  SADNESS = 'sadness',
  LONELINESS = 'loneliness',
  GENERAL_WELLBEING = 'general_wellbeing',
  BEFORE_MEETING = 'before_meeting',
  MORNING_ROUTINE = 'morning_routine',
  EVENING_ROUTINE = 'evening_routine',
  BREAK_TIME = 'break_time'
}

export const AI_SITUATION_LABELS: Record<AISituationType, string> = {
  [AISituationType.ANXIETY_MILD]: 'Ansiedad leve',
  [AISituationType.ANXIETY_MODERATE]: 'Ansiedad moderada',
  [AISituationType.STRESS]: 'Estrés',
  [AISituationType.SLEEP_ISSUES]: 'Problemas de sueño',
  [AISituationType.LOW_MOOD]: 'Estado de ánimo bajo',
  [AISituationType.FOCUS_ISSUES]: 'Dificultad para concentrarse',
  [AISituationType.OVERWHELM]: 'Sentirse abrumado/a',
  [AISituationType.ANGER]: 'Enfado/Irritabilidad',
  [AISituationType.SADNESS]: 'Tristeza',
  [AISituationType.LONELINESS]: 'Soledad',
  [AISituationType.GENERAL_WELLBEING]: 'Bienestar general',
  [AISituationType.BEFORE_MEETING]: 'Antes de una reunión',
  [AISituationType.MORNING_ROUTINE]: 'Rutina de mañana',
  [AISituationType.EVENING_ROUTINE]: 'Rutina de noche',
  [AISituationType.BREAK_TIME]: 'Momento de descanso'
};

// ============================================
// USER PRACTICE FOLDER
// ============================================

export interface UserPracticeFolder {
  id: string;
  userId: string;
  
  name: string;
  description?: string;
  color?: string;          // Hex color
  icon?: string;           // Emoji o nombre de icono
  
  parentId?: string;       // Para subcarpetas
  order: number;           // Para ordenar
  
  practiceCount: number;   // Cache del número de prácticas
  
  createdAt: string;
  updatedAt: string;
}

// ============================================
// DTOs
// ============================================

export interface AddToLibraryDTO {
  userId: string;
  
  source: {
    type: PracticeSourceType;
    therapistId?: string;
    therapistName?: string;
    courseId?: string;
    courseName?: string;
    schoolId?: string;
    schoolName?: string;
    assignmentId?: string;
    originalPracticeId?: string;
  };
  
  practiceData: {
    title: string;
    description: string;
    instructions: string;
    duration: number;
    category: string;
    difficulty: PracticeDifficulty;
    tags?: string[];
    audioUrl?: string;
    videoUrl?: string;
    imageUrl?: string;
  };
  
  aiPermissions?: {
    canBeSuggestedByAI?: boolean;
    approvedSituations?: AISituationType[];
  };
}

export interface UpdateLibraryItemDTO {
  userOrganization?: Partial<UserPracticeLibraryItem['userOrganization']>;
  aiPermissions?: Partial<UserPracticeLibraryItem['aiPermissions']>;
  isActive?: boolean;
  isArchived?: boolean;
}

export interface CreateFolderDTO {
  userId: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  parentId?: string;
}

// ============================================
// LIBRARY FILTERS
// ============================================

export interface LibraryFilters {
  sourceTypes?: PracticeSourceType[];
  categories?: string[];
  tags?: string[];
  isFavorite?: boolean;
  folderId?: string | null;   // null = raíz, undefined = todos
  situations?: AISituationType[];
  searchQuery?: string;
  difficulty?: PracticeDifficulty[];
  isActive?: boolean;
}

export interface LibrarySortOptions {
  field: 'addedAt' | 'lastCompletedAt' | 'timesCompleted' | 'title' | 'avgRating';
  direction: 'asc' | 'desc';
}





